/* @jsx h */
import { defineComponent, h } from "vue";
import { ProductDiscountReward } from "@lemmonhq/types";

export type ProductDiscountRewardEditorProps = {
  value: ProductDiscountReward;
  onChange: (reward: ProductDiscountReward) => void;
};

export const ProductDiscountRewardEditor =
  defineComponent<ProductDiscountRewardEditorProps>({
    name: "ProductDiscountRewardEditor",
    props: ["value", "onChange"],
    setup(props) {
      const updateField = (field: keyof ProductDiscountReward, value: any) => {
        props.onChange({
          ...props.value,
          [field]: value,
        });
      };

      const formatPreview = () => {
        if (props.value.mode === "percentage") {
          return `${props.value.value}% off qualifying products`;
        } else {
          return `$${props.value.value} off qualifying products`;
        }
      };

      return () => (
        <div class="card border bg-base-200">
          <div class="card-body p-4 ">
            <h4 class="card-title text-accent text-lg flex items-center gap-2">
              <div class="w-3 h-3 bg-info rounded-full animate-pulse"></div>
              Product Discount Reward
            </h4>

            <div class="space-y-4 mt-4">
              <div class="form-control">
                <label class="label">
                  <span class="label-text font-medium">Discount Mode</span>
                </label>

                <div class="grid grid-cols-2 gap-3">
                  <label
                    class={`
                  label cursor-pointer p-4 rounded-lg border-2 transition-all
                  ${
                    props.value.mode === "percentage"
                      ? "border-info bg-info/10 shadow-md"
                      : " bg-base-100 hover:border-info/50"
                  }
                `}
                  >
                    <div class="flex items-center space-x-3 w-full">
                      <input
                        type="radio"
                        name="discountMode"
                        value="percentage"
                        checked={props.value.mode === "percentage"}
                        onChange={(e) =>
                          updateField(
                            "mode",
                            (e.target as HTMLInputElement).value,
                          )
                        }
                        class="radio radio-info radio-sm"
                      />
                      <div class="flex-1">
                        <div class="font-medium">Percentage</div>
                        <div class="text-xs text-base-content/70">
                          % off product price
                        </div>
                      </div>
                      <div class="text-2xl">%</div>
                    </div>
                  </label>

                  <label
                    class={`
                  label cursor-pointer p-4 rounded-lg border-2 transition-all
                  ${
                    props.value.mode === "absolute"
                      ? "border-info bg-info/10 shadow-md"
                      : " bg-base-100 hover:border-info/50"
                  }
                `}
                  >
                    <div class="flex items-center space-x-3 w-full">
                      <input
                        type="radio"
                        name="discountMode"
                        value="absolute"
                        checked={props.value.mode === "absolute"}
                        onChange={(e) =>
                          updateField(
                            "mode",
                            (e.target as HTMLInputElement).value,
                          )
                        }
                        class="radio radio-info radio-sm"
                      />
                      <div class="flex-1">
                        <div class="font-medium">Fixed Amount</div>
                        <div class="text-xs text-base-content/70">
                          $ off product price
                        </div>
                      </div>
                      <div class="text-2xl">$</div>
                    </div>
                  </label>
                </div>
              </div>

              <div class="form-control">
                <label class="label">
                  <span class="label-text font-medium">
                    {props.value.mode === "percentage"
                      ? "Discount Percentage"
                      : "Discount Amount"}
                  </span>
                </label>

                <div class="relative">
                  <input
                    type="number"
                    value={props.value.value}
                    onInput={(e) =>
                      updateField(
                        "value",
                        parseFloat((e.target as HTMLInputElement).value) || 0,
                      )
                    }
                    class="input input-bordered focus:input-info w-full"
                    min="0"
                    max={props.value.mode === "percentage" ? 100 : undefined}
                    step={props.value.mode === "percentage" ? 1 : 0.01}
                    placeholder={
                      props.value.mode === "percentage" ? "20" : "5.00"
                    }
                  />
                  <div class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-base-content/50">
                    {props.value.mode === "percentage" ? "%" : "$"}
                  </div>
                </div>

                <label class="label">
                  <span class="label-text-alt">
                    {props.value.mode === "percentage"
                      ? "Enter percentage (1-100)"
                      : "Enter dollar amount"}
                  </span>
                </label>
              </div>
            </div>

            <div class="alert alert-info/20 border-info/30 mt-4">
              <div class="flex items-center gap-2">
                <div class="text-info text-xl">🏷️</div>
                <div>
                  <div class="font-medium text-info">
                    Product Discount Preview
                  </div>
                  <div class="text-sm text-info/80">
                    Customer will get {formatPreview()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    },
  });
